import React, { useCallback, useEffect, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import {
  createServerSchedule, destroyServerSchedule,
  fetchServerSchedules, updateServerSchedule
} from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";
import usePermissions from "src/hooks/usePermissions";
import { formattedDate } from "src/helpers/DateHelpers";

import { Button, Input, Popconfirm, Select, Switch, Tag, Tooltip, message } from 'antd';
import { InboxOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import Card from "src/componets/Card";
import { shortFromNow } from "./_serverHelpers";

type ScheduleAction = 'RESTART' | 'STOP' | 'START' | 'COMMAND' | 'BACKUP';

interface Schedule {
  id: string;
  action: ScheduleAction;
  cron: string;
  payload: string | null;
  enabled: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  createdAt: string;
}

// crafty-servers caps schedules at 10 per server (429 past that).
const MAX_SCHEDULES = 10;

const ACTIONS: { value: ScheduleAction, label: string, hint: string }[] = [
  { value: 'RESTART', label: 'Restart', hint: 'Restart the server' },
  { value: 'STOP',    label: 'Stop',    hint: 'Stop the server' },
  { value: 'START',   label: 'Start',   hint: 'Start the server' },
  { value: 'COMMAND', label: 'Command', hint: 'Run a console command' },
  { value: 'BACKUP',  label: 'Backup',  hint: 'Create a backup' }
];

const ACTION_COLORS: { [key in ScheduleAction]: string } = {
  RESTART: 'blue',
  STOP:    'orange',
  START:   'green',
  COMMAND: 'purple',
  BACKUP:  'gold'
};

// Cron automations on the hosted server: nightly restarts, hourly
// announcements, scheduled backups. The cron expression is validated by
// crafty on save — a bad one comes back as a 400 toast, not silently.
const _Schedules = ({ server }: { server: ServerInterface | undefined }) => {
  const dispatch = useAppDispatch();
  const { write_minecraft } = usePermissions();
  const hosted = server?.hosted_server;

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [action, setAction] = useState<ScheduleAction>('RESTART');
  const [cron, setCron] = useState('');
  const [payload, setPayload] = useState('');

  const serverId = server?.id;

  const load = useCallback(async () => {
    if (!serverId) return;

    setLoading(true);
    const resultAction = await dispatch(fetchServerSchedules(serverId));
    setLoading(false);

    if (fetchServerSchedules.fulfilled.match(resultAction)) {
      setSchedules(resultAction.payload.schedules || []);
    }
  }, [serverId, dispatch]);

  useEffect(() => {
    if (hosted) load().catch(console.error);
  }, [hosted, load]);

  if (!server || !hosted) {
    return (
      <Card>
        <div className='v-srv-empty'>
          <InboxOutlined className='o-icon' />
          <div className='o-title'>Not hosted through crafty-servers</div>
          <div className='o-hint'>Schedules only apply to servers we host.</div>
        </div>
      </Card>
    );
  }

  function fail(payload: any, fallback: string) {
    message.error(Array.isArray(payload) ? payload[0] : fallback);
  }

  const atCap = schedules.length >= MAX_SCHEDULES;
  const needsPayload = action === 'COMMAND';
  const canCreate = !!cron.trim() && (!needsPayload || !!payload.trim()) && !atCap;

  async function create() {
    if (!serverId || !canCreate) return;

    setBusy(true);
    const resultAction = await dispatch(createServerSchedule({
      id:   serverId,
      data: {
        action,
        cron:    cron.trim(),
        payload: needsPayload ? payload.trim() : undefined
      }
    }));
    setBusy(false);

    if (createServerSchedule.fulfilled.match(resultAction)) {
      message.success('Schedule created');
      setCron('');
      setPayload('');
      load().catch(console.error);
    } else {
      fail(resultAction.payload, 'Schedule rejected — check the cron expression');
    }
  }

  async function toggle(schedule: Schedule, enabled: boolean) {
    if (!serverId) return;

    const resultAction = await dispatch(updateServerSchedule({
      id: serverId, scheduleId: schedule.id, data: { enabled }
    }));

    if (updateServerSchedule.fulfilled.match(resultAction)) {
      message.success(enabled ? 'Schedule resumed' : 'Schedule suspended');
      load().catch(console.error);
    } else {
      fail(resultAction.payload, 'Update failed');
    }
  }

  async function remove(schedule: Schedule) {
    if (!serverId) return;

    const resultAction = await dispatch(destroyServerSchedule({ id: serverId, scheduleId: schedule.id }));

    if (destroyServerSchedule.fulfilled.match(resultAction)) {
      message.success('Schedule deleted');
      load().catch(console.error);
    } else {
      fail(resultAction.payload, 'Delete failed');
    }
  }

  return (
    <>
      {write_minecraft &&
        <Card className='v-srv-schedule-form'>
          <h4 className='v-content-title'>New schedule</h4>

          <div className='o-row'>
            <Select
              className='o-action'
              value={action}
              onChange={setAction}
              optionLabelProp='label'
            >
              {ACTIONS.map(option => (
                <Select.Option key={option.value} value={option.value} label={option.label}>
                  <div className='v-srv-option o-compact'>
                    <div className='o-lines'>
                      <div className='o-name'>{option.label}</div>
                      <div className='o-meta'>{option.hint}</div>
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>

            <Input
              className='o-cron'
              placeholder='0 5 * * *'
              value={cron}
              maxLength={64}
              disabled={busy}
              onChange={e => setCron(e.target.value)}
            />

            {needsPayload &&
              <Input
                className='o-payload'
                placeholder='say Automatic save soon'
                value={payload}
                maxLength={512}
                disabled={busy}
                onChange={e => setPayload(e.target.value)}
              />}

            <Tooltip title={atCap ? `Maximum ${MAX_SCHEDULES} schedules per server` : undefined}>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                disabled={busy || !canCreate}
                onClick={create}
              >
                Add
              </Button>
            </Tooltip>
          </div>

          <div className='o-hint'>
            Standard 5-field cron, evaluated in UTC — <code>0 5 * * *</code> is daily at
            05:00, <code>0 * * * *</code> hourly, <code>30 4 * * 1</code> Mondays at 04:30.
          </div>
        </Card>
      }

      <Card className='v-srv-schedules'>
        <div className='o-head'>
          <h4 className='v-content-title'>Schedules</h4>
          <span className='o-count'>{schedules.length}/{MAX_SCHEDULES}</span>
          <Button
            className='o-icon-btn'
            icon={<SyncOutlined />}
            title='Refresh'
            aria-label='Refresh schedules'
            loading={loading}
            onClick={() => load()}
          />
        </div>

        <div className='o-list'>
          {schedules.map(schedule => (
            <div key={schedule.id} className={`o-schedule ${schedule.enabled ? '' : 'o-disabled'}`}>
              <Tag color={ACTION_COLORS[schedule.action] || 'default'}>{schedule.action.toLowerCase()}</Tag>

              <code className='o-cron'>{schedule.cron}</code>

              {schedule.payload && <span className='o-payload'>{schedule.payload}</span>}

              <span className='o-runs'>
                {schedule.lastRunAt && <span>ran {shortFromNow(schedule.lastRunAt)}</span>}
                {schedule.enabled && schedule.nextRunAt &&
                  <span title={formattedDate(schedule.nextRunAt).dateTime}>
                    next {formattedDate(schedule.nextRunAt).dateTime}
                  </span>}
              </span>

              {write_minecraft &&
                <div className='o-controls'>
                  <Switch
                    size='small'
                    checked={schedule.enabled}
                    onChange={value => toggle(schedule, value)}
                    title={schedule.enabled ? 'Suspend without deleting' : 'Resume'}
                  />
                  <Popconfirm
                    title='Delete this schedule? It will never run again.'
                    onConfirm={() => remove(schedule)}
                  >
                    <Button size='small' danger>Delete</Button>
                  </Popconfirm>
                </div>
              }
            </div>
          ))}
        </div>

        {!loading && !schedules.length &&
          <div className='v-srv-empty'>
            <InboxOutlined className='o-icon' />
            <div className='o-title'>No schedules yet</div>
            <div className='o-hint'>
              {write_minecraft
                ? 'Add a nightly restart or a backup schedule above.'
                : 'Cron automations would show up here.'}
            </div>
          </div>
        }
      </Card>
    </>
  );
}

export default _Schedules;
